/**
 * Evolución mensual de pagos — función pura.
 *
 * Construye una serie de los últimos N meses con lo PAGADO y lo PENDIENTE por
 * mes, para graficar una barra apilada (azul = pagado, rojo = pendiente).
 *
 * Se usa tanto en el estado de cuenta del residente ("Mi cuenta", `Cuentas.jsx`)
 * como en la vista del edificio (`BuildingAccountView.jsx`), sobre el mismo
 * criterio de pagado/pendiente que el resto de la app:
 *   - Se excluyen los pagos `status === "rechazado"`.
 *   - Pendiente de un pago = si está `paid` → 0; si no → `remainingAmount ?? amount`.
 *   - Pagado de un pago    = `amount − pendiente` (≥ 0). Así un pago parcial
 *     aporta su parte cobrada al azul y el resto al rojo.
 *   - Total de la barra     = pagado + pendiente = amount (de los no rechazados).
 *
 * El período de cada pago se resuelve por su ETIQUETA `month`/`year` (la del cierre
 * que lo generó) y, si no la tiene, por `maturity` (fecha). Se prioriza la etiqueta
 * porque con fecha de corte configurable el vencimiento cae en el mes siguiente
 * (ej. cierre de septiembre con vencimiento el 10/10) y el pago debe graficarse en
 * SU período (septiembre), no en el del vencimiento.
 */

const MONTH_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const defaultParse = (v) => {
  if (v == null) return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

/**
 * Resuelve el período (mes 0–11, año) de un pago desde `maturity` o `month`/`year`.
 * @returns {{ month:number, year:number } | null}
 */
function resolvePeriod(p) {
  // 1) Etiqueta del cierre (month 1-12 / year) — prioritaria.
  const mm = Number(p.month);
  const yy = Number(p.year);
  if (mm && yy) return { month: mm - 1, year: yy };

  // 2) Fallback: fecha de vencimiento (pagos sin etiqueta, ej. saldos migrados).
  const m = p.maturity;
  if (m) {
    let d = null;
    if (m?.seconds) d = new Date(m.seconds * 1000);
    else if (m instanceof Date) d = m;
    else {
      const s = String(m).trim();
      const dm = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (dm) d = new Date(parseInt(dm[3]), parseInt(dm[2]) - 1, parseInt(dm[1]));
      else {
        const dt = new Date(s);
        if (!isNaN(dt.getTime())) d = dt;
      }
    }
    if (d && !isNaN(d.getTime())) return { month: d.getMonth(), year: d.getFullYear() };
  }
  return null;
}

/**
 * @param {Object}   params
 * @param {Array}    params.payments      - pagos (de un residente o de todo el edificio)
 * @param {number}   [params.months=6]    - cantidad de meses hacia atrás (6 / 12 / 18)
 * @param {Function} [params.parseAmount] - parser de montos (por defecto Number)
 * @param {Date}     [params.now]         - "hoy" (inyectable para test)
 * @returns {Array<{ month:number, year:number, label:string, key:string,
 *   paid:number, pending:number, total:number }>} más antiguo → más reciente
 */
export function computeMonthlyEvolution({ payments = [], months = 6, parseAmount = defaultParse, now = new Date() } = {}) {
  const series = [];
  for (let i = months - 1; i >= 0; i--) {
    const target = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const tMonth = target.getMonth();
    const tYear = target.getFullYear();

    let paid = 0;
    let pending = 0;
    for (const p of payments) {
      if (p.status === "rechazado") continue;
      const per = resolvePeriod(p);
      if (!per || per.month !== tMonth || per.year !== tYear) continue;

      const amount = parseAmount(p.amount);
      const rawPending = p.paid
        ? 0
        : p.remainingAmount != null
        ? parseAmount(p.remainingAmount)
        : amount;
      const pend = Math.max(0, rawPending);
      const paidPortion = Math.max(0, amount - pend);
      paid += paidPortion;
      pending += pend;
    }

    series.push({
      month: tMonth,
      year: tYear,
      label: MONTH_SHORT[tMonth],
      key: `${tMonth}-${tYear}`,
      paid,
      pending,
      total: paid + pending,
    });
  }
  return series;
}

export default computeMonthlyEvolution;
